import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { BEAT_TRANSITION, SHRUNK_OPACITY, SHRUNK_SCALE } from "./beats";

interface ShrinkingBeatProps {
  /** True once a later beat has opened and this one should step back. */
  shrunk: boolean;
  /** Where the shrink pulls toward. Centred screens need `top center`. */
  origin?: "top left" | "top center";
  /**
   * How far the beat dims once superseded. The default steps a played beat
   * back behind the one that replaced it; a screen where the shrunken line
   * goes on doing a job — the offer's headline, which is still the argument
   * the prices under it answer — passes something closer to 1.
   */
  shrunkOpacity?: number;
  children: ReactNode;
}

/**
 * A text beat that shrinks in place to make room for the next one.
 *
 * The mechanic looks trivial but the obvious implementation is wrong.
 * Animating `font-size` re-runs text layout on every frame, so a headline
 * that is two lines at full size and one line when small will visibly
 * re-wrap partway through — words jumping between lines while it moves. That
 * is the jank.
 *
 * Instead the text is scaled. A transform never reflows: the glyph layout is
 * computed once at full size and only its rendering is scaled, so the line
 * breaks are frozen for the whole animation and the work is composited.
 *
 * Scaling alone would leave the element's layout box at full height, though,
 * and the next beat would open into space that hasn't been freed — so the
 * wrapper's height is animated alongside it, from the measured natural height
 * down to that height times the scale. One numeric height animation on a
 * plain box, no text involved.
 */
export default function ShrinkingBeat({
  shrunk,
  origin = "top left",
  shrunkOpacity = SHRUNK_OPACITY,
  children,
}: ShrinkingBeatProps) {
  const inner = useRef<HTMLDivElement>(null);
  const [naturalHeight, setNaturalHeight] = useState<number | null>(null);

  // Measure at full size and keep it current. A ResizeObserver rather than a
  // one-shot read because the height changes when the webfont finishes
  // loading, when the viewport is resized, and when the language changes —
  // all of which alter the line count.
  //
  // The measurement must be transform-independent, which rules out
  // `getBoundingClientRect()`: that reports the *rendered* box, so re-measuring
  // an already-shrunk beat returns the scaled height. The wrapper would then
  // animate to 56% of the shrunken value, the text would overflow the box it
  // no longer fits, and it would land on top of whatever opened underneath.
  // Every trigger listed above (resize, font swap, language change) can fire
  // while shrunk, so this is reachable, and it does not recover on its own.
  // `borderBoxSize` and `offsetHeight` both ignore transforms.
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    setNaturalHeight(el.offsetHeight);
    const ro = new ResizeObserver((entries) => {
      const box = entries[0]?.borderBoxSize?.[0];
      setNaturalHeight(box ? box.blockSize : el.offsetHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <motion.div
      // Until the first measurement lands, height is unset so the box sizes
      // to its content — otherwise the beat would flash at zero height.
      animate={
        naturalHeight == null
          ? {}
          : { height: shrunk ? naturalHeight * SHRUNK_SCALE : naturalHeight }
      }
      transition={BEAT_TRANSITION}
      style={naturalHeight == null ? undefined : { height: naturalHeight }}
    >
      <motion.div
        ref={inner}
        // Anchored so the text shrinks toward its own start rather than
        // drifting in from both sides — which means the centre on a centred
        // screen and the left edge everywhere else.
        style={{ transformOrigin: origin }}
        animate={{
          scale: shrunk ? SHRUNK_SCALE : 1,
          opacity: shrunk ? shrunkOpacity : 1,
        }}
        transition={BEAT_TRANSITION}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
